// Panodan yapıştırma önizlemesi (TODO-KPI-05): "N hücre yazılacak, M atlanacak" + iki liste
// (ilk 10 + "+N daha") → Yapıştır / Vazgeç. ConfirmDialog deseni (role=dialog + aria-modal + Esc +
// odak tuzağı, z 100); açılışta odak Vazgeç'te. Altta `PasteSnackbar`: 5 sn "N hücre yapıştırıldı —
// Geri al" (geri alma eski değerleri yeniden yazar; use-grid-paste.ts). Saf plan lib/kpi-grid-paste.ts.

import { useEffect, useId, useRef } from "react";
import { PASTE_COPY, type PastePlan } from "../../lib/kpi-grid-paste";
import { formatKpiCell } from "../../lib/kpi-ui";

const LIST_CAP = 10;

export interface KpiPasteDialogProps {
  plan: PastePlan;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** DR12 — boş pano satırı uyarısındaki "Boş satırı atla" */
  onSkipEmptyRows?: (() => void) | undefined;
}

export function KpiPasteDialog(props: KpiPasteDialogProps): JSX.Element {
  const { plan, busy, onConfirm, onCancel, onSkipEmptyRows } = props;
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const n = plan.writes.length;
  const m = plan.skipped.length;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-testid="kpi-paste-dialog"
        className="rounded-xl p-6 w-full mx-4"
        style={{
          maxWidth: 560,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          fontFamily: "var(--font-sans)",
        }}
      >
        <h2
          id={titleId}
          style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", marginBottom: 4 }}
        >
          {PASTE_COPY.title}
        </h2>
        <p
          id={bodyId}
          data-testid="kpi-paste-summary"
          style={{ color: "var(--color-text-2)", fontSize: "0.88rem", marginBottom: 14 }}
        >
          {n > 0 ? PASTE_COPY.summary(n, m) : PASTE_COPY.nothing}
          <span className="block text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
            {plan.rows} satır × {plan.cols} sütun panodan okundu
          </span>
        </p>
        {plan.warnings.map((w) => (
          <div
            key={w.kind}
            role="status"
            data-testid="kpi-paste-warning"
            className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 mb-3"
            style={{
              background: "var(--color-warning-bg)",
              border: "1px solid var(--color-warning-border)",
              color: "var(--color-warning-text)",
              fontSize: "0.875rem",
            }}
          >
            <span style={{ flex: "1 1 260px" }}>{PASTE_COPY.emptySourceRow(w.sourceRows)}</span>
            {onSkipEmptyRows ? (
              <button
                type="button"
                onClick={onSkipEmptyRows}
                disabled={busy}
                data-testid="kpi-paste-skip-empty"
                className="rounded-lg px-3 py-1 text-sm font-semibold disabled:opacity-50"
                style={{
                  border: "1px solid var(--color-warning-border)",
                  background: "var(--color-surface)",
                  color: "var(--color-warning-text)",
                  minHeight: 32,
                }}
              >
                {PASTE_COPY.skipEmptyRows}
              </button>
            ) : null}
          </div>
        ))}
        {n > 0 ? (
          <PlanList
            heading={PASTE_COPY.writesHeading}
            tone="write"
            items={plan.writes.map((w) => ({
              key: `${w.pos.row}:${w.pos.col}`,
              label: w.label,
              column: w.columnLabel,
              value: formatKpiCell(
                w.input.status === "filled" && w.input.numericValue !== undefined
                  ? { status: "filled", value: w.input.numericValue, isDerived: false }
                  : w.input.status === "empty"
                    ? undefined
                    : {
                        status: w.input.status,
                        isDerived: false,
                        ...(w.input.textValue !== undefined ? { text: w.input.textValue } : {}),
                      },
              ).text,
            }))}
          />
        ) : null}
        {m > 0 ? (
          <PlanList
            heading={PASTE_COPY.skippedHeading}
            tone="skip"
            items={plan.skipped.map((s, i) => ({
              key: `${s.label}:${s.columnLabel}:${i.toString()}`,
              label: s.label,
              column: s.columnLabel,
              value: `${s.raw || "—"} · ${PASTE_COPY.reasons[s.reason]}`,
            }))}
          />
        ) : null}
        <div className="flex justify-end gap-2 mt-4">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            {PASTE_COPY.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || n === 0}
            aria-busy={busy}
            data-testid="kpi-paste-confirm"
            className="rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            {PASTE_COPY.confirm(n)}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanList(props: {
  heading: string;
  tone: "write" | "skip";
  items: { key: string; label: string; column: string; value: string }[];
}): JSX.Element {
  const { heading, tone, items } = props;
  const shown = items.slice(0, LIST_CAP);
  return (
    <section className="mb-3" aria-label={heading}>
      <div
        className="text-xs font-semibold mb-1"
        style={{
          color: tone === "write" ? "var(--color-primary)" : "var(--color-text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {heading} ({items.length})
      </div>
      <ul
        className="text-xs"
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          background: tone === "write" ? "var(--color-surface)" : "var(--color-bg)",
        }}
      >
        <li
          aria-hidden="true"
          className="flex gap-2 px-2 py-1"
          style={{
            borderBottom: "1px solid var(--color-border)",
            color: "var(--color-text-3)",
            fontWeight: 600,
          }}
        >
          <span style={{ flex: 1 }}>{PASTE_COPY.columns.target}</span>
          <span style={{ flexShrink: 0 }}>{PASTE_COPY.columns.column}</span>
          <span style={{ flexShrink: 0, minWidth: 60, textAlign: "right" }}>
            {PASTE_COPY.columns.value}
          </span>
        </li>
        {shown.map((it) => (
          <li
            key={it.key}
            className="flex gap-2 px-2 py-1"
            style={{ borderBottom: "1px solid var(--color-border)" }}
          >
            {/* hedef satır etiketi zorunlu (DR12) — kırpılırsa tam metin title'da */}
            <span
              className="truncate"
              title={it.label || "—"}
              data-testid="kpi-paste-target"
              style={{ flex: 1, color: "var(--color-text)" }}
            >
              {it.label || "—"}
            </span>
            <span style={{ color: "var(--color-text-3)", flexShrink: 0 }}>{it.column}</span>
            <span
              style={{
                color: "var(--color-text-2)",
                flexShrink: 0,
                fontVariantNumeric: "tabular-nums",
                maxWidth: 200,
              }}
              className="truncate"
            >
              {it.value}
            </span>
          </li>
        ))}
        {items.length > LIST_CAP ? (
          <li className="px-2 py-1" style={{ color: "var(--color-text-3)" }}>
            {PASTE_COPY.more(items.length - LIST_CAP)}
          </li>
        ) : null}
      </ul>
    </section>
  );
}

export interface PasteSnackbarProps {
  message: string;
  onUndo?: (() => void) | undefined;
}

/** Tablo kabının altına sabit (sticky) bilgi şeridi — 5 sn sonra kapanır (zamanlayıcı hook'ta). */
export function PasteSnackbar(props: PasteSnackbarProps): JSX.Element {
  return (
    <div
      role="status"
      data-testid="kpi-paste-snackbar"
      className="text-xs px-3 py-2 flex items-center gap-3"
      style={{
        position: "sticky",
        bottom: 0,
        left: 0,
        background: "var(--color-text)",
        color: "var(--color-surface)",
        borderTop: "1px solid var(--color-border)",
      }}
    >
      <span style={{ flex: 1 }}>{props.message}</span>
      {props.onUndo ? (
        <button
          type="button"
          onClick={props.onUndo}
          className="px-2 py-1 rounded-lg font-semibold"
          style={{ color: "var(--color-surface)", textDecoration: "underline", minHeight: 28 }}
        >
          {PASTE_COPY.undo}
        </button>
      ) : null}
    </div>
  );
}
